"use client";

import { useState } from "react";
import { z } from "zod";

import { BackToSignInFooter } from "@/components/auth/back-to-sign-in-footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormEmail,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
} from "@/components/ui/form";
import { Stack } from "@/components/ui/stack";
import { Typography } from "@/components/ui/typography";

import { fetchApi } from "@/lib/api/client";
import { zRequiredEmail } from "@/lib/form-schema";

const schema = z.object({
  email: zRequiredEmail,
});

export default function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);

  const onSubmit = async (values: z.output<typeof schema>) => {
    const response = await fetchApi("users/send_password_reset_email", {
      method: "POST",
      body: JSON.stringify({ email: values.email }),
    });

    if (response.status === 429) {
      throw new Error(
        "Too many attempts. Please wait a few minutes and try again.",
      );
    }

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || "Failed to send reset link");
    }

    setSentTo(values.email);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl">
          {sentTo ? "Check your email" : "Reset your password"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sentTo ? (
          <Typography size="sm" emphasis="muted" family="sans">
            If {sentTo} has an account, we sent it an email with instructions
            for getting back in. Reset links expire in 15 minutes.
          </Typography>
        ) : (
          <Form
            schema={schema}
            defaultValues={{ email: "" }}
            onSubmit={onSubmit}
          >
            <Stack gap={4}>
              <FormField name="email">
                <FormItem id="email">
                  <FormLabel>Email</FormLabel>
                  <FormEmail required />
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormSubmitButton className="w-full" pending="Sending...">
                Send reset link
              </FormSubmitButton>
            </Stack>
          </Form>
        )}
      </CardContent>
      <BackToSignInFooter />
    </Card>
  );
}
